/**
 * <s-checkout-consent> gates checkout behind the consent checkbox for digital content
 * (early performance of the contract and loss of the right of withdrawal).
 *
 * Required child markup:
 *   input[type=checkbox][data-consent-checkbox] — `required` checkbox named
 *       attributes[...] inside the cart form, so native validation blocks the checkout
 *       submit and a normal checkout submit carries the consent as a cart attribute.
 *   [data-consent-gated] — Regions (accelerated checkout buttons such as PayPal or
 *       Shop Pay) that stay inert until the checkbox is checked.
 *
 * Element dataset:
 *   data-cart-update-url — routes.cart_update_url from Liquid; the consent is also written
 *       to the cart attribute so accelerated checkouts that skip the form carry it.
 *   data-attribute-name — Cart attribute key, identical to the checkbox's attribute key.
 *   data-recorded — "true" when the cart already holds the attribute on render.
 *
 * State is shared between instances on the same page (cart page and cart drawer) and
 * survives partial re-renders. It is not persisted across page loads, so a stale
 * attribute from an earlier visit is cleared until the customer agrees again.
 */
let consentGiven = false;
const instances = new Set();

class CheckoutConsent extends HTMLElement {
  #ac;

  connectedCallback() {
    const checkbox = this.#checkbox;
    if (!checkbox) return;

    this.#ac = new AbortController();
    instances.add(this);

    checkbox.checked = consentGiven;
    this.#sync();

    if (!consentGiven && this.dataset.recorded === 'true') {
      this.#record(false);
    }

    checkbox.addEventListener('change', () => this.#onChange(), { signal: this.#ac.signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
    instances.delete(this);
  }

  get #checkbox() {
    return this.querySelector('[data-consent-checkbox]');
  }

  #sync() {
    const checkbox = this.#checkbox;
    if (!checkbox) return;
    checkbox.checked = consentGiven;
    for (const region of this.querySelectorAll('[data-consent-gated]')) {
      region.inert = !consentGiven;
      region.classList.toggle('opacity-50', !consentGiven);
      region.classList.toggle('grayscale', !consentGiven);
    }
  }

  async #onChange() {
    consentGiven = this.#checkbox.checked;
    for (const instance of instances) instance.#sync();
    await this.#record(consentGiven);
  }

  async #record(given) {
    const url = this.dataset.cartUpdateUrl;
    const name = this.dataset.attributeName;
    if (!url || !name) return;

    try {
      await fetch(`${url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { [name]: given ? this.#checkbox.value : '' } }),
      });
    } catch {
      // The checkout submit still carries the checkbox value; nothing else to recover here.
    }
  }
}

if (!customElements.get('s-checkout-consent')) {
  customElements.define('s-checkout-consent', CheckoutConsent);
}
